import { ChangeDetectionStrategy, Component, Input, type OnInit, inject } from '@angular/core';
import { map, type Observable, Subject, take } from 'rxjs';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Actions, ofType } from '@ngrx/effects';

import type { Id, Uuid } from '@common/types';
import type { SiteEntity, SiteListEntity } from '@common/types/site.type';
import type { CollectionStateStatuses } from '@common/adapters/collections';

import type { DenormalizedBlockEntity } from '@web/types';

import { SitesCollectionFacade } from '@web/store/collections/sites/sites.facade';

import BlocksActions from '@web/store/page-structure/blocks/blocks.actions';
import { NgSelectModule } from '@ng-select/ng-select';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { NgFor, NgIf, AsyncPipe, SlicePipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

@Component({
    selector: 'web-copy-to-page-modal',
    templateUrl: './copy-to-page-modal.component.html',
    styleUrls: ['./copy-to-page-modal.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgSelectModule, ReactiveFormsModule, FormsModule, NgFor, NgIf, AsyncPipe, SlicePipe, TranslateModule],
})
export class CopyToPageModalComponent implements OnInit {
    @Input() public block: DenormalizedBlockEntity;
    @Input() public currentSiteId: SiteEntity['id'];
    @Input() public currentPageId: Id | Uuid;

    private readonly bsModalRef: BsModalRef = inject(BsModalRef);
    private readonly actions$: Actions = inject(Actions);
    private readonly sitesCollectionFacade: SitesCollectionFacade = inject(SitesCollectionFacade);

    public readonly sites$: Observable<SiteListEntity[]> = this.sitesCollectionFacade.sites$.pipe(
        map((sites) => {
            if (!sites?.length) {
                return [];
            }

            return sites
                .map((site) => ({
                    ...site,
                    pages: site.pages.filter((page) => page.id != this.currentPageId),
                }))
                .filter((site) => site.pages.length > 0)
                .sort((a, b) => (a.id == this.currentSiteId ? -1 : b.id == this.currentSiteId ? -1 : 1));
        })
    );
    public readonly sitesStatuses$: Observable<CollectionStateStatuses> = this.sitesCollectionFacade.sitesStatuses$;

    public onClose: Subject<false | number[]>;

    public pageIds: number[] = [];
    public loading: boolean = false;

    public ngOnInit(): void {
        this.onClose = new Subject();

        this.actions$.pipe(ofType(BlocksActions.copyBlockToPageSuccess), take(1)).subscribe(() => {
            this.bsModalRef.hide();
        });
    }

    public onConfirm(): void {
        this.onClose.next(this.pageIds);
        this.loading = true;
    }

    public onCancel(): void {
        if (this.loading) {
            return;
        }

        this.onClose.next(false);
        this.bsModalRef.hide();
    }

    public onCloseModal(): void {
        if (this.loading) {
            return;
        }

        this.bsModalRef.hide();
    }
}
